import { apiRequest } from './client.js';

/**
 * Delegation (M02 §4 Role Delegation) — real /v1/delegations contract.
 * A delegator temporarily grants one or more of their roles to a delegate
 * for a bounded [effectiveFrom, effectiveTo] window. Delegations can be
 * listed (both directions) and revoked before they expire.
 */

/** §4 lifecycle states a delegation can hold. */
export const DELEGATION_STATUSES = ['ACTIVE', 'REVOKED', 'EXPIRED'] as const;
export type DelegationStatus = (typeof DELEGATION_STATUSES)[number];

export const DELEGATION_STATUS_LABELS: Readonly<Record<string, string>> = {
  ACTIVE: 'Active',
  REVOKED: 'Revoked',
  EXPIRED: 'Expired',
};

/** T025 delegation record as returned by the real backend. */
export interface Delegation {
  readonly delegationId: string;
  readonly delegatorUserId: string;
  readonly delegatorName: string | null;
  readonly delegateUserId: string;
  readonly delegateName: string | null;
  readonly delegatedRoleIds: string[];
  readonly delegatedRoleNames: string[];
  readonly reason: string | null;
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
  readonly recordStatusCode: DelegationStatus;
  readonly revocationReason?: string | null;
  readonly revokedAt?: string | null;
  readonly createdAt?: string;
}

export interface CreateDelegationInput {
  readonly delegateUserId: string;
  readonly delegatedRoleIds: string[];
  readonly reason?: string;
  /** ISO 8601 datetime with timezone (V-DT-01). */
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
}

type Tok = string | null | undefined;

function unwrap(
  r: { items: readonly Delegation[] } | readonly Delegation[] | null,
): readonly Delegation[] {
  if (!r) return [];
  return Array.isArray(r) ? r : (r as { items: readonly Delegation[] }).items ?? [];
}

/** GET /v1/delegations — delegations the current user created (as delegator). */
export async function listMyDelegations(token?: Tok): Promise<readonly Delegation[]> {
  const r = await apiRequest<{ items: readonly Delegation[] } | readonly Delegation[]>(
    '/delegations',
    { token },
  );
  return unwrap(r);
}

/** GET /v1/delegations/received — active delegations granted TO the current user. */
export async function listReceivedDelegations(token?: Tok): Promise<readonly Delegation[]> {
  const r = await apiRequest<{ items: readonly Delegation[] } | readonly Delegation[]>(
    '/delegations/received',
    { token },
  );
  return unwrap(r);
}

/** POST /v1/delegations — create a delegation (§4 full DTO). */
export function createDelegation(body: CreateDelegationInput, token?: Tok): Promise<Delegation> {
  return apiRequest<Delegation>('/delegations', { method: 'POST', token, body });
}

/** POST /v1/delegations/:delegationId/revoke — revoke an active delegation. */
export function revokeDelegation(delegationId: string, token?: Tok): Promise<Delegation> {
  return apiRequest<Delegation>(
    `/delegations/${encodeURIComponent(delegationId)}/revoke`,
    { method: 'POST', token },
  );
}
